// Global Instructions Rule Applied!

import { isAction, type Middleware } from '@reduxjs/toolkit'
import type { UpdateDealRecordAction, AddDealAction } from '../interfaces/deals.ts'
import type { UpdateContactRecordAction, AddContactAction } from '../interfaces/contacts.ts'
import type { UpdateCompanyRecordAction, AddCompanyAction } from '../interfaces/companies.ts'
import type { UpdateProspectRecordAction, AddProspectAction } from '../interfaces/fundraising.ts'
import { UPDATE_DEAL_RECORD, ADD_DEAL } from '../state_update_actions/deals/constants.ts'
import { UPDATE_CONTACT_RECORD, ADD_CONTACT } from '../state_update_actions/contacts/constants.ts'
import { UPDATE_COMPANY_RECORD, ADD_COMPANY } from '../state_update_actions/companies/constants.ts'
import { UPDATE_PROSPECT_RECORD, ADD_PROSPECT } from '../state_update_actions/fundraising/constants.ts'
import { updateActiveRecord, setActiveRecord } from '../state_update_actions/active_records/active_record_actions.ts'
import { DealFields } from '../data_models/deal_record.ts'
import { ContactRecordFields } from '../data_models/contact_record.ts'
import { AccountRecordFields } from '../data_models/account_record.ts'
import { ProspectRecordFields } from '../data_models/prospect_record.ts'

/** The record actions this middleware mirrors into the active-records slice. */
type ActiveRecordsAction =
  | UpdateDealRecordAction
  | AddDealAction
  | UpdateContactRecordAction
  | AddContactAction
  | UpdateCompanyRecordAction
  | AddCompanyAction
  | UpdateProspectRecordAction
  | AddProspectAction

const HANDLED_TYPES = new Set<string>([
  UPDATE_DEAL_RECORD,
  ADD_DEAL,
  UPDATE_CONTACT_RECORD,
  ADD_CONTACT,
  UPDATE_COMPANY_RECORD,
  ADD_COMPANY,
  UPDATE_PROSPECT_RECORD,
  ADD_PROSPECT
])

const isActiveRecordsAction = (action: unknown): action is ActiveRecordsAction =>
  isAction(action) && HANDLED_TYPES.has(action.type)

export const activeRecordsMiddleware: Middleware = (store) => (next) => (action) => {
  const result = next(action)

  if (!isActiveRecordsAction(action)) {
    return result
  }

  switch (action.type) {
    case UPDATE_DEAL_RECORD: {
      const { dealId, updates } = action.payload
      const dealIdString = dealId.toString()

      // Check if updates contains the deal ID field - indicates a full record refresh
      const updatesIdField = updates[DealFields.deals__id] || updates.deals__id || updates.id
      const updateFieldCount = Object.keys(updates).length
      const isFullRecordRefresh =
        (updatesIdField === dealId || updatesIdField === String(dealId)) && updateFieldCount > 5

      // If it's a full record refresh, fully replace the record (not merge)
      // Otherwise, merge updates for backward compatibility with partial updates
      if (isFullRecordRefresh) {
        store.dispatch(setActiveRecord({ uuid: dealIdString, record: updates }))
      } else {
        store.dispatch(updateActiveRecord({ uuid: dealIdString, updates }))
      }
      break
    }

    case ADD_DEAL: {
      const { deal } = action.payload
      const dealId = deal[DealFields.deals__id] || deal.deals__id || deal.id
      if (dealId) {
        store.dispatch(setActiveRecord({ uuid: dealId.toString(), record: deal }))
      }
      break
    }

    case UPDATE_CONTACT_RECORD: {
      const { contactId, updates } = action.payload
      const contactIdString = contactId.toString()

      // Check if updates contains the contact ID field - indicates a full record refresh
      const updatesIdField = updates[ContactRecordFields.contacts__id] || updates.contacts__id || updates.id
      const updateFieldCount = Object.keys(updates).length
      const isFullRecordRefresh =
        (updatesIdField === contactId || updatesIdField === String(contactId)) && updateFieldCount > 5

      // If it's a full record refresh, fully replace the record (not merge)
      // Otherwise, merge updates for backward compatibility with partial updates
      if (isFullRecordRefresh) {
        store.dispatch(setActiveRecord({ uuid: contactIdString, record: updates }))
      } else {
        store.dispatch(updateActiveRecord({ uuid: contactIdString, updates }))
      }
      break
    }

    case ADD_CONTACT: {
      const { contact } = action.payload
      const contactId = contact[ContactRecordFields.contacts__id] || contact.contacts__id || contact.id
      if (contactId) {
        store.dispatch(setActiveRecord({ uuid: contactId.toString(), record: contact }))
      }
      break
    }

    case UPDATE_COMPANY_RECORD: {
      const { companyId, updates } = action.payload
      const companyIdString = companyId.toString()

      // Check if updates contains the company ID field - indicates a full record refresh
      const updatesIdField = updates[AccountRecordFields.accounts__id] || updates.accounts__id || updates.id
      const updateFieldCount = Object.keys(updates).length
      const isFullRecordRefresh =
        (updatesIdField === companyId || updatesIdField === String(companyId)) && updateFieldCount > 5

      // If it's a full record refresh, fully replace the record (not merge)
      // Otherwise, merge updates for backward compatibility with partial updates
      if (isFullRecordRefresh) {
        store.dispatch(setActiveRecord({ uuid: companyIdString, record: updates }))
      } else {
        store.dispatch(updateActiveRecord({ uuid: companyIdString, updates }))
      }
      break
    }

    case ADD_COMPANY: {
      const { company } = action.payload
      const companyId = company[AccountRecordFields.accounts__id] || company.accounts__id || company.id
      if (companyId) {
        store.dispatch(setActiveRecord({ uuid: companyId.toString(), record: company }))
      }
      break
    }

    case UPDATE_PROSPECT_RECORD: {
      const { prospectId, updates } = action.payload
      const prospectIdString = prospectId.toString()

      // Check if updates contains the prospect ID field - indicates a full record refresh
      // ProspectFields.prospects__id is 'prospects__id'
      const updatesIdField = updates['prospects__id'] || updates.prospects__id || updates.id
      const updateFieldCount = Object.keys(updates).length
      const isFullRecordRefresh =
        (updatesIdField === prospectId || updatesIdField === String(prospectId)) && updateFieldCount > 5

      // If it's a full record refresh, fully replace the record (not merge)
      // Otherwise, merge updates for backward compatibility with partial updates
      if (isFullRecordRefresh) {
        store.dispatch(setActiveRecord({ uuid: prospectIdString, record: updates }))
      } else {
        store.dispatch(updateActiveRecord({ uuid: prospectIdString, updates }))
      }
      break
    }

    case ADD_PROSPECT: {
      const { prospect } = action.payload
      const prospectId = prospect[ProspectRecordFields.prospects__id] || prospect.prospects__id || prospect.id
      if (prospectId) {
        store.dispatch(setActiveRecord({ uuid: prospectId.toString(), record: prospect }))
      }
      break
    }
  }

  return result
}
